import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, forwardRef, HostListener, ElementRef, ViewChild, ChangeDetectorRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface SelectOption {
  value: any;
  label: string;
  disabled?: boolean;
}

@Component({
  selector: 'app-searchable-select',
  templateUrl: './searchable-select.component.html',
  styleUrls: ['./searchable-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchableSelectComponent),
      multi: true
    }
  ]
})
export class SearchableSelectComponent implements OnInit, OnChanges, ControlValueAccessor {
  @Input() options: SelectOption[] = [];
  @Input() placeholder: string = 'Select an option';
  @Input() searchPlaceholder: string = 'Search...';
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() allowClear: boolean = false;
  @Input() displayKey?: string; // For object options, key to display
  @Input() valueKey?: string; // For object options, key for value
  @Input() searchCallback?: (searchTerm: string) => Promise<any[]> | any[]; // Optional API search callback
  @Input() minSearchLength: number = 0; // Minimum characters before triggering search (0 = search on open/empty)
  @Input() searchDebounce: number = 300; // Debounce time in ms for search
  @Input() prefetchOnOpen = true; // Fetch first page when dropdown opens (async mode)
  @Input() pinnedOptions: SelectOption[] = []; // Always shown (e.g. "All", selected value)
  @Input() emptySearchHint = 'Type to search...';
  
  @Output() selectionChange = new EventEmitter<any>();
  @Output() searchChange = new EventEmitter<string>(); // Emit search term changes
  
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
  @ViewChild('dropdown') dropdown!: ElementRef<HTMLDivElement>;

  isOpen = false;
  searchTerm = '';
  selectedOption: SelectOption | null = null;
  filteredOptions: SelectOption[] = [];
  highlightedIndex = -1;
  isSearching = false;
  private searchTimeout: any = null;
  private searchGeneration = 0;

  private onChange = (value: any) => {};
  private onTouched = () => {};

  private currentValue: any = null;
  private isSelecting = false; // Flag to prevent writeValue from interfering

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.filteredOptions = this.options || [];
    // Update selected option based on current value
    if (this.currentValue !== null && this.currentValue !== undefined) {
      this.writeValue(this.currentValue);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options']) {
      this.filteredOptions = this.options || [];
      // Update selected option when options change
      if (this.currentValue !== null && this.currentValue !== undefined) {
        this.writeValue(this.currentValue);
      } else {
        this.updateSelectedOption();
      }
    }
  }

  get displayValue(): string {
    if (this.selectedOption) {
      return this.selectedOption.label;
    }
    return '';
  }

  getDisplayText(): string {
    if (this.selectedOption && this.selectedOption.label) {
      return this.selectedOption.label;
    }
    return this.placeholder;
  }

  get filteredOptionsList(): SelectOption[] {
    const base = this.searchCallback
      ? this.filteredOptions
      : (!this.searchTerm
          ? this.filteredOptions
          : this.filteredOptions.filter(opt =>
              opt.label.toLowerCase().includes(this.searchTerm.toLowerCase()) && !opt.disabled
            ));
    return this.mergePinnedOptions(base);
  }

  private mergePinnedOptions(options: SelectOption[]): SelectOption[] {
    if (!this.pinnedOptions.length) return options;
    const merged = [...this.pinnedOptions];
    for (const opt of options) {
      if (!merged.some(existing => this.compareValues(existing.value, opt.value))) {
        merged.push(opt);
      }
    }
    return merged;
  }

  isOptionSelected(option: SelectOption): boolean {
    if (!this.selectedOption) {
      return false;
    }
    return this.compareValues(this.selectedOption.value, option.value);
  }

  toggleDropdown(): void {
    if (this.disabled) return;
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.searchTerm = '';
      this.highlightedIndex = -1;
      if (this.searchCallback) {
        if (this.prefetchOnOpen) {
          this.performSearch('');
        } else {
          this.filteredOptions = [];
        }
      } else {
        this.filteredOptions = this.options || [];
      }
      setTimeout(() => {
        if (this.searchInput) {
          this.searchInput.nativeElement.focus();
        }
      }, 0);
    }
  }

  closeDropdown(): void {
    this.isOpen = false;
    this.searchTerm = '';
    this.highlightedIndex = -1;
  }

  selectOption(option: SelectOption, event?: Event): void {
    if (option.disabled) return;
    
    // Set flag immediately to prevent HostListener from interfering
    this.isSelecting = true;
    
    // Stop event propagation to prevent document click from closing dropdown
    if (event) {
      event.stopPropagation();
      event.preventDefault();
      event.stopImmediatePropagation();
    }
    
    // CRITICAL: Set selected option FIRST
    this.selectedOption = {
      value: option.value,
      label: option.label,
      disabled: option.disabled
    };
    this.currentValue = option.value;
    
    // Force change detection BEFORE calling onChange
    this.cdr.detectChanges();
    
    // Update the form control value - this might trigger writeValue
    this.onChange(option.value);
    this.onTouched();
    
    // Emit selection change event
    this.selectionChange.emit(option.value);
    
    // Close dropdown
    this.closeDropdown();
    
    // Clear flag after a delay to allow writeValue to complete
    setTimeout(() => {
      this.isSelecting = false;
      // Ensure selectedOption is still set after writeValue might have run
      if (!this.selectedOption || !this.compareValues(this.selectedOption.value, option.value)) {
        this.selectedOption = {
          value: option.value,
          label: option.label,
          disabled: option.disabled
        };
      }
      this.cdr.detectChanges();
    }, 50);
  }

  clearSelection(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    this.selectedOption = null;
    this.currentValue = null;
    this.onChange(null);
    this.onTouched();
    this.selectionChange.emit(null);
  }

  onSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const term = input.value;
    this.searchTerm = term;
    this.highlightedIndex = -1;
    this.searchChange.emit(term);

    // Clear existing timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    // If search callback is provided
    if (this.searchCallback) {
      if (term.length === 0 && this.prefetchOnOpen) {
        this.isSearching = true;
        this.searchTimeout = setTimeout(() => this.performSearch(''), this.searchDebounce);
      } else if (term.length >= this.minSearchLength) {
        this.isSearching = true;
        this.searchTimeout = setTimeout(() => {
          this.performSearch(term);
        }, this.searchDebounce);
      } else if (term.length > 0 && term.length < this.minSearchLength) {
        this.filteredOptions = [];
      }
    }
  }

  private async performSearch(term: string): Promise<void> {
    const requestId = ++this.searchGeneration;
    try {
      const results = await this.searchCallback!(term);
      if (requestId !== this.searchGeneration) return;
      if (Array.isArray(results)) {
        // Update options with search results
        this.filteredOptions = results.map(item => {
          if (typeof item === 'object' && this.displayKey && this.valueKey) {
            return {
              value: item[this.valueKey],
              label: item[this.displayKey]
            };
          }
          // Assume it's already a SelectOption
          return item;
        });
      }
    } catch (error) {
      if (requestId !== this.searchGeneration) return;
      console.error('Search error:', error);
      // Keep existing filtered options on error
    } finally {
      if (requestId === this.searchGeneration) {
        this.isSearching = false;
      }
    }
  }

  onKeyDown(event: KeyboardEvent): void {
    if (!this.isOpen) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.toggleDropdown();
      }
      return;
    }

    const filtered = this.filteredOptionsList;
    
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.highlightedIndex = Math.min(this.highlightedIndex + 1, filtered.length - 1);
        this.scrollToHighlighted();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.highlightedIndex = Math.max(this.highlightedIndex - 1, -1);
        this.scrollToHighlighted();
        break;
      case 'Enter':
        event.preventDefault();
        if (this.highlightedIndex >= 0 && filtered[this.highlightedIndex]) {
          this.selectOption(filtered[this.highlightedIndex]);
        }
        break;
      case 'Escape':
        event.preventDefault();
        this.closeDropdown();
        break;
    }
  }

  scrollToHighlighted(): void {
    setTimeout(() => {
      const element = document.getElementById(`option-${this.highlightedIndex}`);
      if (element && this.dropdown) {
        element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }, 0);
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event): void {
    if (!this.isOpen) return;
    
    // Don't close if currently selecting (give time for click to register)
    if (this.isSelecting) return;
    
    const target = event.target as Node;
    if (this.dropdown?.nativeElement && !this.dropdown.nativeElement.contains(target)) {
      this.closeDropdown();
    }
  }

  updateSelectedOption(): void {
    // Update selected option based on current value from ControlValueAccessor
    // This is called when options change
    if (this.selectedOption) {
      // Check if the selected option still exists in the options list
      const stillExists = this.options.find(opt => this.compareValues(opt.value, this.selectedOption!.value));
      if (!stillExists) {
        // Selected option no longer exists, try to find it by value
        const currentValue = this.selectedOption.value;
        this.selectedOption = this.options.find(opt => this.compareValues(opt.value, currentValue)) || null;
      }
    }
  }

  compareValues(val1: any, val2: any): boolean {
    // Handle null/undefined - treat both null and undefined as equal
    const isVal1Null = (val1 === null || val1 === undefined || val1 === '');
    const isVal2Null = (val2 === null || val2 === undefined || val2 === '');
    
    if (isVal1Null && isVal2Null) {
      return true;
    }
    if (isVal1Null || isVal2Null) {
      return false;
    }
    
    // Handle objects - compare by reference or by id if available
    if (typeof val1 === 'object' && typeof val2 === 'object') {
      // If both have id property, compare by id
      if (val1.id !== undefined && val2.id !== undefined) {
        return val1.id === val2.id;
      }
      // Otherwise compare by reference
      return val1 === val2;
    }
    
    // Primitive comparison
    // Handle numeric equivalence between string/number (e.g. '1' === 1)
    if (typeof val1 === 'number' || typeof val2 === 'number') {
      const n1 = Number(val1);
      const n2 = Number(val2);
      if (!isNaN(n1) && !isNaN(n2)) {
        return n1 === n2;
      }
    }

    // Fallback to string comparison for other primitive values
    return String(val1) === String(val2);
  }

  // ControlValueAccessor implementation
  writeValue(value: any): void {
    // CRITICAL: Don't interfere if we're in the middle of selecting
    if (this.isSelecting) {
      return;
    }
    
    // Don't overwrite if value hasn't changed
    if (this.currentValue === value && this.selectedOption) {
      return;
    }
    
    this.currentValue = value;
    
    // Find option by comparing values (including null/undefined)
    const allOptions = this.mergePinnedOptions(this.options || []);
    if (allOptions.length > 0) {
      const foundOption = allOptions.find(opt => this.compareValues(opt.value, value));
      if (foundOption) {
        this.selectedOption = foundOption;
        this.cdr.detectChanges();
      } else if (value === null || value === undefined) {
        // Explicitly handle null/undefined
        this.selectedOption = allOptions.find(opt => opt.value === null || opt.value === undefined) || null;
        this.cdr.detectChanges();
      } else {
        // Value not found in options - keep current if it matches, otherwise clear
        if (!this.selectedOption || !this.compareValues(this.selectedOption.value, value)) {
          this.selectedOption = null;
          this.cdr.detectChanges();
        }
      }
    } else {
      // No options available
      if (value === null || value === undefined) {
        this.selectedOption = null;
      } else if (!this.selectedOption || !this.compareValues(this.selectedOption.value, value)) {
        this.selectedOption = null;
      }
      this.cdr.detectChanges();
    }
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
